import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { arrow, autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, } from "react";
import { createPortal } from "react-dom";
export const TooltipContext = createContext(null);
export const FADE_MS = 150;
const ARROW_GAP = 8;
const VIEWPORT_MARGIN = 8;
const ARROW_SIZE = 10;
const SIDE_ORDER = ["top", "bottom", "right", "left"];
const fallbacksFor = (placement) => {
    const [side, align] = placement.split("-");
    return SIDE_ORDER.filter((s) => s !== side).map((s) => (align ? `${s}-${align}` : s));
};
const ARROW_ROTATION = {
    top: "rotate(135deg)",
    bottom: "rotate(-45deg)",
    left: "rotate(45deg)",
    right: "rotate(-135deg)",
};
const ARROW_EDGE = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
};
const SURFACE = "bg-[var(--colour-surface-container-default)] border-[var(--colour-border-default)]";
const TEXT = "text-[14px] leading-[20px] font-normal [word-break:break-word]";
const ICON_SLOT = "inline-flex items-center justify-center w-[16px] h-[16px] shrink-0";
const hasContent = (node) => node !== undefined && node !== null && node !== false && node !== "";
const isElement = (anchor) => "getBoundingClientRect" in anchor;
const rectOf = (anchor) => isElement(anchor) ? anchor.getBoundingClientRect() : DOMRect.fromRect({ x: anchor.left, y: anchor.top, ...anchor });
const centreOf = (anchor) => {
    const r = rectOf(anchor);
    return DOMRect.fromRect({ x: r.left + r.width / 2, y: r.top + r.height / 2, width: 0, height: 0 });
};
/**
 * Design-system tooltip: a bordered card with a pointer, portalled to `document.body`
 * and positioned against its anchor by Floating UI. It slides along the preferred
 * edge to stay in the viewport, moves to another side only when that is not enough,
 * follows the anchor through scrolling and resizing, and fades over 150ms. Showing
 * and hiding belong to the caller: {@link TooltipTrigger} for the hover case, or a
 * conditional render around a standalone `<Tooltip anchor={...}>`.
 *
 * @example
 * <TooltipTrigger><button>Save</button><Tooltip>Save the curve</Tooltip></TooltipTrigger>
 * @example
 * {invalid && <Tooltip title="Please choose a unique name" anchor={tabRef.current} />}
 */
export const Tooltip = ({ anchor: anchorProp, placement, origin, interactive: interactiveProp, arrow: withArrow = true, title, children, leadingIcon, actions, "data-testid": testId, }) => {
    const ctx = useContext(TooltipContext);
    const anchor = anchorProp ?? ctx?.anchor ?? null;
    const preferred = placement ?? ctx?.placement ?? "top";
    const from = origin ?? ctx?.origin ?? "edge";
    const interactive = Boolean(actions) || (interactiveProp ?? ctx?.interactive ?? false);
    const shouldShow = (ctx ? ctx.open : true) && anchor !== null && (hasContent(title) || hasContent(children));
    const [mounted, setMounted] = useState(shouldShow);
    const [state, setState] = useState("closed");
    useEffect(() => {
        if (shouldShow) {
            setMounted(true);
            const id = setTimeout(() => setState("open"), 16);
            return () => clearTimeout(id);
        }
        setState("closed");
        const id = setTimeout(() => setMounted(false), FADE_MS);
        return () => clearTimeout(id);
    }, [shouldShow]);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, placement: resolved, middlewareData, } = useFloating({
        placement: preferred,
        strategy: "fixed",
        open: mounted,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(ARROW_GAP),
            flip({ fallbackPlacements: fallbacksFor(preferred), crossAxis: false }),
            shift({ padding: VIEWPORT_MARGIN }),
            arrow({ element: arrowRef, padding: ARROW_SIZE }),
        ],
    });
    useLayoutEffect(() => {
        if (!anchor)
            return;
        if (from === "center") {
            refs.setReference({
                getBoundingClientRect: () => centreOf(anchor),
                contextElement: isElement(anchor) ? anchor : undefined,
            });
        }
        else if (isElement(anchor))
            refs.setReference(anchor);
        else
            refs.setReference({ getBoundingClientRect: () => rectOf(anchor) });
    }, [anchor, from, refs]);
    if (!mounted)
        return null;
    const side = resolved.split("-")[0];
    const primary = hasContent(title) ? title : children;
    const secondary = hasContent(title) ? children : undefined;
    const arrowStyle = {
        left: middlewareData.arrow?.x,
        top: middlewareData.arrow?.y,
        [ARROW_EDGE[side]]: -ARROW_SIZE / 2,
        transform: ARROW_ROTATION[side],
    };
    return createPortal(_jsx("div", { ref: refs.setFloating, "data-state": state, style: { ...floatingStyles, zIndex: 1000000, pointerEvents: interactive ? undefined : "none" }, className: "transition-opacity duration-[150ms] data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 data-[state=open]:opacity-100", ...(interactive ? ctx?.bubbleProps : undefined), children: _jsxs("div", { id: ctx?.id, role: "tooltip", "data-artis-ui": true, "data-placement": resolved, "data-testid": testId, className: `relative flex max-w-[300px] flex-col rounded-[8px] border px-[16px] py-[8px] shadow-[1px_4px_8px_0_rgba(0,0,0,0.15)] ${SURFACE} ${TEXT}`, children: [withArrow && (_jsx("span", { ref: arrowRef, "data-arrow": true, className: `absolute h-[10px] w-[10px] border-t border-r ${SURFACE}`, style: arrowStyle })), hasContent(primary) && (_jsxs("div", { "data-part": "title", className: "flex gap-[4px] text-[var(--colour-text-default)]", children: [leadingIcon && _jsx("span", { className: ICON_SLOT, children: leadingIcon }), _jsx("span", { children: primary })] })), hasContent(secondary) && (_jsxs("div", { "data-part": "body", className: "flex gap-[4px] text-[var(--colour-text-light)]", children: [leadingIcon && _jsx("span", { className: ICON_SLOT }), _jsx("span", { children: secondary })] })), actions && _jsx("div", { className: "flex w-full justify-end pt-[4px]", children: actions })] }) }), document.body);
};
//# sourceMappingURL=Tooltip.js.map